/**
 * Client-side edits to a document's translations (CanonicalDocument.localization, schema v30). The
 * editor keeps translations in its own document state and saves them with the layout; the server
 * stays the authority on which strings exist (translations/inspect) and on what renders.
 *
 * isWellFormed mirrors TranslatableStrings.IsWellFormed (C#) so a bad edit is flagged as it is typed;
 * the server re-checks on render and falls back to the source text with a warning.
 */
import type { DocumentLocalization, LocaleTranslations, TranslationEntry, TranslationOverride } from './types'
import { TranslationStatus } from './types'
import { normalizeMonikerKey } from './monikerKey'

const TOKEN = /\{\{(.*?)\}\}|<(\d{1,4})>|<\/(\d{1,4})>|<(\d{1,4})\/>/g

export function emptyLocalization(): DocumentLocalization {
  return { defaultLanguage: null, payloadLocaleKey: null, locales: [] }
}

/**
 * True when the translation carries exactly the source's variables and tags, each once, with the
 * source's tag structure: a tag nested in the source (formatted words inside a link,
 * `<1>the <2>full</2> terms</1>`) must stay inside the same tag, and nothing else may nest.
 */
export function isWellFormed(source: string, translation: string): boolean {
  const placeholders: string[] = []
  const paired = new Set<number>()
  const self = new Set<number>()
  const parent = new Map<number, number>()
  const sourceOpen: number[] = []
  for (const m of source.matchAll(TOKEN)) {
    if (m[1] !== undefined) placeholders.push(normalizeMonikerKey(m[1].trim()))
    else if (m[2] !== undefined) {
      const n = Number(m[2])
      paired.add(n)
      if (sourceOpen.length) parent.set(n, sourceOpen[sourceOpen.length - 1])
      sourceOpen.push(n)
    } else if (m[3] !== undefined) sourceOpen.pop()
    else if (m[4] !== undefined) self.add(Number(m[4]))
  }
  const remaining = [...placeholders]
  const used = new Set<number>()
  const open: number[] = []
  for (const m of translation.matchAll(TOKEN)) {
    if (m[1] !== undefined) {
      const i = remaining.indexOf(normalizeMonikerKey(m[1].trim()))
      if (open.length || i < 0) return false
      remaining.splice(i, 1)
    } else if (m[2] !== undefined) {
      const n = Number(m[2])
      if (!paired.has(n) || used.has(n)) return false
      if ((parent.get(n) ?? null) !== (open.length ? open[open.length - 1] : null)) return false
      used.add(n); open.push(n)
    } else if (m[3] !== undefined) {
      if (!open.length || open[open.length - 1] !== Number(m[3])) return false
      open.pop()
    } else {
      const n = Number(m[4])
      if (open.length || !self.has(n) || used.has(n)) return false
      used.add(n)
    }
  }
  return open.length === 0 && remaining.length === 0 && used.size === paired.size + self.size
}

const sameTag = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

export function findLocale(loc: DocumentLocalization | null | undefined, locale: string): LocaleTranslations | undefined {
  return loc?.locales.find(l => sameTag(l.locale, locale))
}

export function addLocale(loc: DocumentLocalization | null | undefined, locale: string): DocumentLocalization {
  const base = loc ?? emptyLocalization()
  if (findLocale(base, locale)) return base
  return { ...base, locales: [...base.locales, { locale, entries: [], overrides: [] }] }
}

export function removeLocale(loc: DocumentLocalization | null | undefined, locale: string): DocumentLocalization {
  const base = loc ?? emptyLocalization()
  return { ...base, locales: base.locales.filter(l => !sameTag(l.locale, locale)) }
}

export function setDefaultLanguage(loc: DocumentLocalization | null | undefined, language: string | null): DocumentLocalization {
  return { ...(loc ?? emptyLocalization()), defaultLanguage: language || null }
}

/** Upserts one translation; an empty text removes it. */
export function putEntry(
  loc: DocumentLocalization | null | undefined, locale: string,
  key: string, source: string, text: string, status: TranslationStatus = TranslationStatus.Accepted,
): DocumentLocalization {
  const withLocale = addLocale(loc, locale)
  return {
    ...withLocale,
    locales: withLocale.locales.map(l => {
      if (!sameTag(l.locale, locale)) return l
      const rest = l.entries.filter(e => e.key !== key)
      if (!text) return { ...l, entries: rest }
      const entry: TranslationEntry = { key, source, text, status }
      const at = l.entries.findIndex(e => e.key === key)
      return { ...l, entries: at < 0 ? [...rest, entry] : l.entries.map(e => (e.key === key ? entry : e)) }
    }),
  }
}

/**
 * Upserts a translation of one occurrence (schema v33); an empty text removes it, so that occurrence
 * uses the shared translation again.
 */
export function putOverride(
  loc: DocumentLocalization | null | undefined, locale: string,
  key: string, context: string, source: string, text: string, status: TranslationStatus = TranslationStatus.Accepted,
): DocumentLocalization {
  const withLocale = addLocale(loc, locale)
  return {
    ...withLocale,
    locales: withLocale.locales.map(l => {
      if (!sameTag(l.locale, locale)) return l
      const current = l.overrides ?? []
      const same = (o: TranslationOverride) => o.key === key && o.context === context
      if (!text) return { ...l, overrides: current.filter(o => !same(o)) }
      const override: TranslationOverride = { key, context, source, text, status }
      const at = current.findIndex(same)
      return { ...l, overrides: at < 0 ? [...current, override] : current.map(o => (same(o) ? override : o)) }
    }),
  }
}

export function findOverride(table: LocaleTranslations | undefined, key: string, context: string): TranslationOverride | undefined {
  return table?.overrides?.find(o => o.key === key && o.context === context && o.text)
}

export function acceptAllDrafts(loc: DocumentLocalization | null | undefined, locale: string): DocumentLocalization {
  const base = loc ?? emptyLocalization()
  const accept = <T extends { status: TranslationStatus }>(x: T): T => (x.status === TranslationStatus.Draft ? { ...x, status: TranslationStatus.Accepted } : x)
  return {
    ...base,
    locales: base.locales.map(l => sameTag(l.locale, locale)
      ? { ...l, entries: l.entries.map(accept), ...(l.overrides ? { overrides: l.overrides.map(accept) } : {}) }
      : l),
  }
}

// ── CSV / JSON (same formats as the API's export and import) ─────────────────

/** One line of an export; `context` set = a per-occurrence translation. */
export interface ExportRow { key: string; context?: string | null; source: string; text: string | null; status: string }

const csvCell = (s: string) => (/[",\r\n]/.test(s) || s.startsWith(' ') || s.endsWith(' ') ? `"${s.replace(/"/g, '""')}"` : s)

/** Same layout as the API's export: `key,context,source,translation,status`. */
export function toCsv(rows: ExportRow[]): string {
  return 'key,context,source,translation,status\r\n' +
    rows.map(r => [csvCell(r.key), csvCell(r.context ?? ''), csvCell(r.source), csvCell(r.text ?? ''), r.status].join(',')).join('\r\n') + '\r\n'
}

export function toJson(rows: ExportRow[], sourceLanguage: string | null, targetLanguage: string): string {
  return JSON.stringify({
    version: 1, sourceLanguage, targetLanguage,
    units: rows.map(r => ({ key: r.key, context: r.context ?? null, source: r.source, target: r.text, status: r.status })),
  }, null, 2)
}

/** One imported translation; `context` set = a per-occurrence translation. */
export interface ImportUnit { key: string; text: string; draft: boolean; context?: string }

function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } else quoted = false
      } else field += c
      continue
    }
    if (c === '"' && field.length === 0) quoted = true
    else if (c === ',') { row.push(field); field = '' }
    else if (c === '\n') { row.push(field); field = ''; rows.push(row); row = [] }
    else if (c !== '\r') field += c
  }
  if (quoted) throw new Error('Unterminated quoted field in CSV.')
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row) }
  if (rows[0]?.[0]?.startsWith('﻿')) rows[0][0] = rows[0][0].slice(1)
  return rows.filter(r => r.length > 1 || (r.length === 1 && r[0] !== ''))
}

/** Parses an exported file. Rows without a translation are skipped. Throws on a malformed file. */
export function parseImport(content: string, format: 'csv' | 'json'): ImportUnit[] {
  if (format === 'json') {
    let root: unknown
    try { root = JSON.parse(content) } catch { throw new Error('Malformed JSON.') }
    const units = Array.isArray(root) ? root : (root as { units?: unknown })?.units
    if (!Array.isArray(units)) throw new Error('Expected an object with a "units" array.')
    return units
      .filter((u): u is Record<string, unknown> => !!u && typeof u === 'object')
      .map(u => withContext({ key: String(u.key ?? ''), text: String(u.target ?? u.translation ?? ''), draft: u.status === 'draft' },
        typeof u.context === 'string' ? u.context : ''))
      .filter(u => u.key && u.text)
  }
  const rows = parseCsv(content)
  if (rows.length === 0) return []
  const header = rows[0].map(h => h.trim().toLowerCase())
  const k = header.indexOf('key')
  const t = header.indexOf('translation') >= 0 ? header.indexOf('translation') : header.indexOf('target')
  const s = header.indexOf('status')
  const c = header.indexOf('context')
  if (k < 0 || t < 0) throw new Error('The CSV header must include "key" and "translation" columns.')
  return rows.slice(1)
    .map(r => withContext({ key: (r[k] ?? '').trim(), text: r[t] ?? '', draft: s >= 0 && (r[s] ?? '').trim().toLowerCase() === 'draft' },
      c >= 0 ? r[c] ?? '' : ''))
    .filter(u => u.key && u.text)
}

function withContext(u: ImportUnit, context: string): ImportUnit {
  const trimmed = context.trim()
  return trimmed ? { ...u, context: trimmed } : u
}

/** A short list of common locales for the picker; any BCP-47 tag can still be typed. */
export const COMMON_LOCALES: Array<{ tag: string; label: string }> = [
  { tag: 'en-US', label: 'English (United States)' }, { tag: 'en-GB', label: 'English (United Kingdom)' },
  { tag: 'fr-FR', label: 'French (France)' }, { tag: 'fr-CA', label: 'French (Canada)' },
  { tag: 'de-DE', label: 'German (Germany)' }, { tag: 'es-ES', label: 'Spanish (Spain)' },
  { tag: 'es-MX', label: 'Spanish (Mexico)' }, { tag: 'it-IT', label: 'Italian' },
  { tag: 'pt-BR', label: 'Portuguese (Brazil)' }, { tag: 'pt-PT', label: 'Portuguese (Portugal)' },
  { tag: 'nl-NL', label: 'Dutch' }, { tag: 'sv-SE', label: 'Swedish' }, { tag: 'pl-PL', label: 'Polish' },
  { tag: 'ar-SA', label: 'Arabic' }, { tag: 'he-IL', label: 'Hebrew' },
]

/** Canonical casing for a typed tag (fr_ca → fr-CA), or null when it does not look like one. */
export function normalizeLocaleTag(raw: string): string | null {
  const tag = raw.trim().replace(/_/g, '-')
  if (!/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(tag)) return null
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? null
  } catch {
    return null
  }
}
